import { v4 as uuid4 } from 'uuid';
import { CommentAnnotationPlugin } from '../comment-annotation/plugin/comment-annotation.plugin';
import { onAddingAnnotationDataChanged } from '../../services/annotation.service';

export interface BaseDraggableMarkerParams {
  range: { start: number | null; end: number | null };
  instanceId: string;
  annotationPlugin: any;
  targetElement: HTMLElement | JQuery<HTMLElement>;
  player: any;
  forceRangeContainer?: boolean;
}

export class BaseDraggableMarkerComponent {
  range: { start: number | null; end: number | null } = { start: 0, end: 0 };
  instanceId: string;
  annotationPlugin: CommentAnnotationPlugin;
  containerId: string;
  $parent: JQuery<HTMLElement>;
  dragging: boolean = false;
  clickTime: any;
  markerDuration: number;
  player: any;
  private readonly singlePointWidthPx = 18;
  private readonly forceRangeContainer: boolean;
  private isProgrammaticSeeking: boolean = false;

  constructor(draggableMarkerParams: BaseDraggableMarkerParams) {
    window['drag'] = this;
    this.range = draggableMarkerParams.range;
    this.instanceId = draggableMarkerParams.instanceId;
    this.annotationPlugin = draggableMarkerParams.annotationPlugin;
    this.player = draggableMarkerParams.player;
    this.forceRangeContainer = !!draggableMarkerParams.forceRangeContainer;

    // $parent is the targetElement
    this.$parent = $(draggableMarkerParams.targetElement);
    this.containerId = uuid4();

    if (this.$parent.length === 0) {
      throw new Error('No valid target element provided for draggable marker');
    }

    if (this.$parent.length > 0) {
      this.initTemplate();
      this.render();
      this.bindEvents();
    }
  }

  initTemplate() {
    // Create marker HTML structure and append to $parent (targetElement)
    let $markerContainer = $("<div class='draggable-marker-container' id=" + this.containerId + '></div>');
    let $draggableContainer = $("<div class='draggable-marker'></div>");
    let $leftArrow = $("<div class='before-arrow'><i class='fa fa-play'></i></div>");
    let $rightArrow = $("<div class='after-arrow'><i class='fa fa-play'></i></div>");

    $markerContainer.append($draggableContainer, $leftArrow, $rightArrow);
    this.$parent.append($markerContainer);
  }

  render() {
    const start = this.getSafeStart();
    const end = this.getSafeEnd();
    const width = Math.max(0, end - start);
    const widthPct = this.getPercentage(width);
    const isSinglePoint = this.range.end === null;
    const $container = $('#' + this.containerId);
    const $after = $container.find('.after-arrow');
    const $before = $container.find('.before-arrow');
    const $marker = $container.find('.draggable-marker');

    $container.css('left', this.getPercentage(start) + '%');
    if (isSinglePoint) {
      if (!this.forceRangeContainer) {
        $container.css({
          width: `${this.singlePointWidthPx}px`,
          height: `${this.singlePointWidthPx}px`,
          'border-radius': '50%',
          'align-items': 'center',
          display: 'flex',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        });
      } else {
        $container.css({
          width: '',
          height: '',
          'border-radius': '',
          'align-items': '',
          display: '',
          backgroundColor: '',
        });
      }
      $after.hide();
      $before.hide();
      $marker.css({
        width: `${this.singlePointWidthPx}px`,
        height: `${this.singlePointWidthPx}px`,
        'border-radius': '50%',
        margin: '0 auto',
        backgroundColor: '#ffffff',
        top: this.forceRangeContainer ? '45%' : 'auto',
        position: this.forceRangeContainer ? 'absolute' : 'inherit',
        left: this.forceRangeContainer ? '-7px' : 'auto',
        opacity: 1,
      });
    } else {
      $container.css({
        width: widthPct + '%',
        height: '',
        'border-radius': '',
        'align-items': '',
        display: '',
        backgroundColor: '',
      });
      $after.show();
      $before.show();
      $marker.css({
        width: '100%',
        height: '100%',
        'border-radius': '',
        backgroundColor: 'black',
        top: 'auto',
        position: 'inherit',
        left: 'auto',
        opacity: 0.7,
      });
    }
  }

  getPercentage(val: number): number {
    return (val / this.duration()) * 100;
  }

  duration(): number {
    let duration: number;
    if (typeof this.player.getDuration === 'function') {
      duration = this.player.getDuration();
    } else {
      duration = this.player.duration();
    }
    return duration;
  }

  currentTime(): number {
    let currentTime: number;
    if (typeof this.player.getCurrentTime === 'function') {
      currentTime = this.player.getCurrentTime();
    } else {
      currentTime = this.player.currentTime();
    }
    return currentTime;
  }

  private pausePlayerIfPlaying(): void {
    try {
      if (this.player) {
        if (typeof this.player.paused === 'function' && !this.player.paused()) {
          this.player.pause();
          return;
        }
        if (typeof this.player.pause === 'function') {
          this.player.pause();
        }
      }
    } catch (_) {}
  }

  private syncPlayerTimeToStart(): void {
    const nextStart = this.getSafeStart();
    this.seekPlayerTo(nextStart);
  }

  private syncPlayerTimeToEnd(): void {
    const nextEnd = this.getSafeEnd();
    this.seekPlayerTo(nextEnd);
  }

  private seekPlayerTo(time: number): void {
    if (Number.isNaN(time)) {
      return;
    }

    // Set flag to prevent timeUpdateChange from moving start time during seek
    this.isProgrammaticSeeking = true;

    if (typeof this.player?.setCurrentTime === 'function') {
      this.player.setCurrentTime(time);
    } else if (typeof this.player?.currentTime === 'function') {
      this.player.currentTime(time);
    } else if (typeof this.player?.seekTo === 'function') {
      const duration = this.duration();
      if (duration > 0) {
        const ratio = Math.min(Math.max(time / duration, 0), 1);
        this.player.seekTo(ratio);
      }
    }

    // Reset flag after a short delay to allow seek event to complete
    setTimeout(() => {
      this.isProgrammaticSeeking = false;
    }, 100);
  }

  percentValFromXpos(xpos: number): number {
    var offset = this.$parent.offset();
    if (!offset) return 0;

    var x = Math.max(0, xpos - offset.left);
    var max = this.$parent.innerWidth() || 0;
    var per = x / max;

    if (per > 1) per = 1;
    if (per < 0) per = 0;
    return per;
  }

  bindEvents() {
    let _this = this;

    // ON DRAG ON BEFORE ICON (Start handle)
    $('#' + this.containerId + ' .before-arrow').on('mousedown', function (e) {
      e.preventDefault();
      _this.pausePlayerIfPlaying();
      _this.dragging = true;
      $(document).on('mousemove.' + _this.containerId + '.before-arrow', (event) => {
        if (_this.dragging) _this.onBeforeDrag(event);
      });
    });

    $(document).off('mouseup.' + this.containerId + '.before-arrow');
    $(document).on('mouseup.' + this.containerId + '.before-arrow', function (e) {
      if (!_this.dragging) return;
      _this.pausePlayerIfPlaying();
      $(document).off('mousemove.' + _this.containerId + '.before-arrow');
      setTimeout(function () {
        _this.dragging = false;
      }, 250);
    });

    // ON DRAG ON AFTER ICON (End handle)
    $('#' + this.containerId + ' .after-arrow').on('mousedown', function (e) {
      e.preventDefault();
      _this.pausePlayerIfPlaying();
      _this.dragging = true;
      $(document).on('mousemove.' + _this.containerId + '.after-arrow', (event) => {
        if (_this.dragging) _this.onAfterDrag(event);
      });
    });

    $(document).off('mouseup.' + this.containerId + '.after-arrow');
    $(document).on('mouseup.' + this.containerId + '.after-arrow', function (e) {
      if (!_this.dragging) return;
      _this.pausePlayerIfPlaying();
      $(document).off('mousemove.' + _this.containerId + '.after-arrow');
      setTimeout(function () {
        _this.dragging = false;
      }, 250);
    });

    // On Drag Entire Marker
    $('#' + this.containerId + ' .draggable-marker').on('mousedown', function (e) {
      e.preventDefault();
      _this.pausePlayerIfPlaying();
      _this.dragging = true;
      _this.clickTime = _this.duration() * _this.percentValFromXpos(e.pageX);
      _this.markerDuration = _this.getSafeEnd() - _this.getSafeStart();
      $(document).on('mousemove.' + _this.containerId + '.draggable-marker', (event) => {
        if (_this.dragging) _this.onMarkerDrag(event);
      });
    });

    $(document).off('mouseup.' + this.containerId + '.draggable-marker');
    $(document).on('mouseup.' + this.containerId + '.draggable-marker', function (e) {
      if (!_this.dragging) return;
      _this.pausePlayerIfPlaying();
      $(document).off('mousemove.' + _this.containerId + '.draggable-marker');
      setTimeout(function () {
        _this.dragging = false;
      }, 250);
    });
  }

  onMarkerDrag(e: any) {
    const hadNullEnd = this.range.end === null;
    let markerDuration = this.markerDuration;
    let dragPercent = this.percentValFromXpos(e.pageX);
    let secVal = this.duration() * dragPercent;
    let timeToMove = this.clickTime - secVal;
    this.clickTime = secVal;

    let start = this.getSafeStart() - timeToMove;
    let end = this.getSafeEnd() - timeToMove;

    if (start < 0) start = 0;
    if (start > this.duration() - markerDuration) start = this.duration() - markerDuration;

    if (end > this.duration()) end = this.duration();
    if (end < markerDuration) end = markerDuration;

    this.range.start = start;
    this.range.end = hadNullEnd ? null : end;

    this.render();
    this.fireAnnotationDataChanged();
    this.syncPlayerTimeToStart();
  }

  onBeforeDrag(e: any) {
    let dragPercent = this.percentValFromXpos(e.pageX);
    let secVal = this.duration() * dragPercent;

    const end = this.getSafeEnd();
    if (secVal < end) {
      this.range.start = secVal;
    } else if (secVal >= end) {
      this.range.start = end;
    }

    this.render();
    this.fireAnnotationDataChanged();
    this.syncPlayerTimeToStart();
  }

  onAfterDrag(e: any) {
    let dragPercent = this.percentValFromXpos(e.pageX);
    let secVal = this.duration() * dragPercent;

    const start = this.getSafeStart();
    if (secVal < start) {
      this.range.end = start;
    } else if (secVal >= start) {
      this.range.end = secVal;
    }

    this.render();
    this.fireAnnotationDataChanged();
    this.syncPlayerTimeToEnd();
  }

  // Utility methods for common marker operations
  timeUpdateChange() {
    // Skip if we're in the middle of a programmatic seek to avoid moving start time
    if (this.isProgrammaticSeeking) {
      return;
    }
    // When a range is set, start is fixed — only update in single-point mode
    if (this.range.end !== null) {
      return;
    }
    const nextStart = Math.round(this.currentTime() * 10) / 10;
    if (this.range.start !== null) {
      this.range.start = nextStart;
    }
    this.render();
    this.fireAnnotationDataChanged();
  }

  onPlay() {
    const nextStart = Math.round(this.currentTime() * 10) / 10;
    this.range.start = nextStart;
    this.range.end = nextStart;
    $('#' + this.containerId).hide();
    this.render();
    this.fireAnnotationDataChanged();
  }

  onBreak() {
    const nextStart = Math.round(this.currentTime() * 10) / 10;
    this.range.start = nextStart;
    this.range.end = nextStart;
    $('#' + this.containerId).show();
    this.render();
    this.fireAnnotationDataChanged();
  }

  updateStart(change: number) {
    const safeStart = this.getSafeStart();
    let newStart = safeStart + change;
    if (newStart < 0) {
      this.range.start = 0;
    } else if (newStart > this.duration()) {
      this.range.start = this.duration();
    } else {
      this.range.start = newStart;
    }

    if (this.range.end !== null && this.range.start >= this.range.end) {
      this.range.end = this.range.start;
    }

    this.render();
    this.fireAnnotationDataChanged();
    this.syncPlayerTimeToStart();
  }

  updateEnd(change: number) {
    const baseEnd = this.range.end ?? this.range.start ?? 0;
    let newEnd = baseEnd + change;

    if (newEnd < 0) {
      this.range.end = 0;
    } else if (newEnd > this.duration()) {
      this.range.end = this.duration();
    } else {
      this.range.end = newEnd;
    }

    if (this.range.start !== null && this.range.end !== null && this.range.end < this.range.start) {
      this.range.start = this.range.end;
    }

    if (this.range.start === null) {
      this.range.start = this.range.end;
    }

    this.render();
    this.fireAnnotationDataChanged();
    this.syncPlayerTimeToEnd();
  }

  show() {
    $('#' + this.containerId).show();
  }

  hide() {
    $('#' + this.containerId).hide();
  }

  fireAnnotationDataChanged() {
    onAddingAnnotationDataChanged.emit({
      range: this.range,
      itemId: this.annotationPlugin.getItem?.()?.id,
      instanceId: this.instanceId,
    });
  }

  setStartTime(value: number | null): void {
    const nextStart = this.clampToDuration(value ?? 0);
    this.range.start = nextStart;
    if (this.range.end !== null && this.range.end < nextStart) {
      this.range.end = nextStart;
    }
    this.render();
    this.fireAnnotationDataChanged();
    this.syncPlayerTimeToStart();
  }

  setEndTime(value: number | null): void {
    if (value === null || value === undefined) {
      this.range.end = null;
      this.render();
      this.fireAnnotationDataChanged();
      return;
    }

    const nextEnd = this.clampToDuration(value);
    this.range.end = nextEnd;
    if (this.range.start !== null && this.range.start > nextEnd) {
      this.range.start = nextEnd;
    }
    this.render();
    this.fireAnnotationDataChanged();
    this.syncPlayerTimeToEnd();
  }

  private getSafeStart(): number {
    const start = this.range.start ?? 0;
    return Number.isFinite(start) ? start : 0;
  }

  private getSafeEnd(): number {
    const end = this.range.end;
    const start = this.getSafeStart();
    if (end === null || end === undefined) {
      return start;
    }
    return Number.isFinite(end) ? end : start;
  }

  private clampToDuration(value: number): number {
    if (value < 0) {
      return 0;
    }
    if (value > this.duration()) {
      return this.duration();
    }
    return value;
  }

  teardown() {
    // Remove all event listeners
    $('#' + this.containerId + ' .before-arrow').off('mousedown');
    $('#' + this.containerId + ' .after-arrow').off('mousedown');
    $('#' + this.containerId + ' .draggable-marker').off('mousedown');

    $(document).off('mousemove.' + this.containerId + '.before-arrow');
    $(document).off('mouseup.' + this.containerId + '.before-arrow');
    $(document).off('mousemove.' + this.containerId + '.after-arrow');
    $(document).off('mouseup.' + this.containerId + '.after-arrow');
    $(document).off('mousemove.' + this.containerId + '.draggable-marker');
    $(document).off('mouseup.' + this.containerId + '.draggable-marker');

    // Remove DOM element
    let $markerContainer = $(document.getElementById(this.containerId) as HTMLElement);
    $markerContainer.remove();
  }
}
